import { 
    Box, 
    Paper, 
    Typography, 
    Grid, 
    Chip, 
    Avatar,
    Divider
} from "@mui/material"
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PersonIcon from '@mui/icons-material/Person';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import SecurityIcon from '@mui/icons-material/Security';
import StorageIcon from '@mui/icons-material/Storage';
import CategoryIcon from '@mui/icons-material/Category';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SourceIcon from '@mui/icons-material/Source';
import GavelIcon from '@mui/icons-material/Gavel';
import BuildIcon from '@mui/icons-material/Build';
import PlaceIcon from '@mui/icons-material/Place';
import ShareIcon from '@mui/icons-material/Share';
import FlagIcon from '@mui/icons-material/Flag';
import DescriptionIcon from '@mui/icons-material/Description';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { getLegalBases, getDataCategories } from 'services/mapping-service';
import { FILTER } from '../filter-types';

export const ReportsRopaProcess = ({process, filter, data, category, legalBase, processing, position}) => {
    const { t, i18n } = useTranslation('mapping');
    const selectedTenant = useSelector(useAuthTenant);

    const { data: legalBasesList } = useQuery(
        ['getLegalBases', selectedTenant?.slug, i18n.language],
        async () => {
            if (!selectedTenant?.slug) return [];
            const result = await getLegalBases(selectedTenant.slug);
            return result?.data?.data ?? [];
        },
    );

    const { data: dataCategoriesList } = useQuery(
        ['getDataCategories', selectedTenant?.slug, i18n.language],
        async () => {
            if (!selectedTenant?.slug) return [];
            const result = await getDataCategories(selectedTenant.slug);
            return result?.data?.data ?? [];
        },
    );

    const legalBaseByKey = new Map((legalBasesList ?? []).map((lb) => [lb.key, lb.name]));
    const categoryByKey = new Map((dataCategoriesList ?? []).map((c) => [c.key, c.name]));
    // Calcular estatísticas
    const totalData = process.dataMapping?.length || 0;
    const totalProcessing = process.dataMapping?.reduce((acc, dm) => 
        acc + (dm.dataProcessing?.length || 0), 0) || 0;

    // Filtrar dados
    const filteredData = process.dataMapping?.filter((dataMap) => {
        if (filter === FILTER.DATA && !!data) {
            return (dataMap.name) === data
        }
        if (filter === FILTER.CATEGORY && !!category) {
            return (dataMap.category) === category
        }
        if (filter === FILTER.PROCESSING && !!processing) {
            return dataMap.dataProcessing.some((itemDataProcess) => {
                return (itemDataProcess.processing) === processing
            })
        }
        if (filter === FILTER.LEGAL_BASE && !!legalBase) {
            return dataMap.dataProcessing.some((itemDataProcess) => {
                return (itemDataProcess.legalBase) === legalBase
            })
        }
        if (filter === FILTER.POSITION && !!position) {
            return dataMap.dataProcessing.some((itemDataProcess) => {
                return (itemDataProcess.position) === position
            })
        }
        return true
    });

    return (
        <Paper 
            elevation={1}
            sx={{ 
                mt: 2, 
                mb: 2,
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid #e8e8e8',
                ml: { xs: 0, md: 3 }
            }}
        >
            {/* Header do Processo */}
            <Box 
                sx={{ 
                    p: 2.5, 
                    background: 'linear-gradient(135deg, #d4e8f9 0%, #b8d9f7 100%)',
                    color: '#3a4a5a'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Avatar sx={{ bgcolor: 'rgba(58,74,90,0.15)', width: 40, height: 40, mr: 2 }}>
                        <AccountTreeIcon sx={{ color: '#6b7999' }} />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="overline" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                            {t('ropa.process.label')}
                        </Typography>
                        <Typography variant="h6" component="h3" fontWeight="bold">
                            {process.name}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={1.5}>
                    <Grid item xs={12} md={6}>
                        <Box 
                            sx={{ 
                                p: 1.5, 
                                bgcolor: 'rgba(58,74,90,0.1)', 
                                borderRadius: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <PersonIcon sx={{ fontSize: 20, color: '#6b7999' }} />
                            <Box>
                                <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                    {t('ropa.process.responsible')}
                                </Typography>
                                <Typography variant="body2" fontWeight="600">
                                    {process.user || t('ropa.process.notDefined')}
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'rgba(58,74,90,0.1)', borderRadius: 1.5 }}>
                            <DataUsageIcon sx={{ fontSize: 20, mb: 0.3, color: '#6b7999' }} />
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                {totalData}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                                {t('ropa.process.data')}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center', p: 1.5, bgcolor: 'rgba(58,74,90,0.1)', borderRadius: 1.5 }}>
                            <SecurityIcon sx={{ fontSize: 20, mb: 0.3, color: '#6b7999' }} />
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.1rem' }}>
                                {totalProcessing}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.65rem' }}>
                                {t('ropa.process.processings')}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Descrição do Fluxo */}
            {process.descriptionDataFlow && (
                <Box sx={{ p: 2.5, bgcolor: '#f5f8fe' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <DescriptionIcon sx={{ mr: 1, color: '#a5b8d4', fontSize: 18 }} />
                        <Typography variant="subtitle2" fontWeight="bold" color="#6b7999">
                            {t('ropa.process.dataFlowDescription')}
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                        {process.descriptionDataFlow}
                    </Typography>
                </Box>
            )}

            {/* Dados Mapeados */}
            <Box sx={{ p: 2 }}>
                {!!process.dataMapping && filteredData?.map((dataMap, index) => (
                    <motion.div
                        key={`${dataMap.name}${index}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                        <Paper 
                            elevation={0}
                            sx={{ 
                                mb: 2, 
                                border: '2px solid #e8e8e8',
                                borderRadius: 2,
                                overflow: 'hidden'
                            }}
                        >
                            {/* Header do Dado */}
                            <Box 
                                sx={{ 
                                    p: 2, 
                                    bgcolor: '#b3e0f7',
                                    background: 'linear-gradient(135deg, #d4eef9 0%, #b8e5f7 100%)',
                                    color: '#3a5a6a'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Avatar sx={{ bgcolor: 'rgba(58,90,106,0.15)', width: 32, height: 32, mr: 1.5 }}>
                                        <DataUsageIcon sx={{ fontSize: 18, color: '#6b8a99' }} />
                                    </Avatar>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {dataMap.name}
                                    </Typography>
                                </Box>
                                <Chip
                                    label={categoryByKey.get(dataMap.category) ?? dataMap.category}
                                    size="small"
                                    icon={<CategoryIcon sx={{ fontSize: 16, color: '#6b8a99 !important' }} />}
                                    sx={{ 
                                        bgcolor: 'rgba(58,90,106,0.2)', 
                                        color: '#3a5a6a',
                                        fontWeight: 600,
                                        '& .MuiChip-icon': { color: '#6b8a99' }
                                    }}
                                />
                            </Box>

                            {/* Informações do Dado */}
                            <Grid container sx={{ p: 2, bgcolor: '#f8fcfe' }}>
                                <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <SourceIcon sx={{ color: '#8eb3c9', fontSize: 20, mt: 0.2 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                {t('ropa.process.origin')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                {dataMap.dataSource || '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <PersonOutlineIcon sx={{ color: '#8eb3c9', fontSize: 20, mt: 0.2 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                {t('ropa.process.holderProfile')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                {dataMap.holderProfile || '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <AccessTimeIcon sx={{ color: '#8eb3c9', fontSize: 20, mt: 0.2 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                {t('ropa.process.retention')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                {dataMap.retentionPeriod || '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        <StorageIcon sx={{ color: '#8eb3c9', fontSize: 20, mt: 0.2 }} />
                                        <Box>
                                            <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                {t('ropa.process.storage')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                {dataMap.storage || '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Tratamentos */}
                            {dataMap.dataProcessing && dataMap.dataProcessing.filter((dataProcess) => {
                                if (filter === FILTER.PROCESSING && !!processing) {
                                    return dataProcess.processing === processing
                                }
                                if (filter === FILTER.LEGAL_BASE && !!legalBase) {
                                    return dataProcess.legalBase === legalBase
                                }
                                if (filter === FILTER.POSITION && !!position) {
                                    return dataProcess.position === position
                                }
                                return true
                            }).map((dataProcess, dpIndex) => (
                                <Box key={`${dataProcess.processing}-${dpIndex}`}>
                                    <Divider />
                                    <Box 
                                        sx={{ 
                                            p: 1.5, 
                                            bgcolor: '#b8e6c9',
                                            background: 'linear-gradient(90deg, #d4f1e0 0%, #bce8cd 100%)',
                                            color: '#3a5a4a'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <SecurityIcon sx={{ mr: 1, fontSize: 18, color: '#6b9979' }} />
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                {t('ropa.process.processing')}: {dataProcess.processing}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Grid container sx={{ p: 2, bgcolor: '#f9fefb' }}>
                                        <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <FlagIcon sx={{ color: '#8eb3a9', fontSize: 20, mt: 0.2 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                        {t('ropa.process.finality')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                        {dataProcess.finality || '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <PlaceIcon sx={{ color: '#8eb3a9', fontSize: 20, mt: 0.2 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                        {t('ropa.process.position')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                        {dataProcess.position || '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <GavelIcon sx={{ color: '#8eb3a9', fontSize: 20, mt: 0.2 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                        {t('ropa.process.legalBase')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                        {legalBaseByKey.get(dataProcess.legalBase) ?? dataProcess.legalBase ?? '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        {dataProcess.complementLegalBasis && (
                                            <Grid item xs={12} sx={{ p: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                    <DescriptionIcon sx={{ color: '#8eb3a9', fontSize: 20, mt: 0.2 }} />
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                            {t('ropa.process.complementLegalBasis')}
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                            {dataProcess.complementLegalBasis}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <BuildIcon sx={{ color: '#8eb3a9', fontSize: 20, mt: 0.2 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                        {t('ropa.process.tool')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                        {dataProcess.tool || '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <PlaceIcon sx={{ color: '#8eb3a9', fontSize: 20, mt: 0.2 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                        {t('ropa.process.destiny')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                        {dataProcess.destiny || '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4} sx={{ p: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <ShareIcon sx={{ color: '#8eb3a9', fontSize: 20, mt: 0.2 }} />
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary" fontWeight="600">
                                                        {t('ropa.process.shareType')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ mt: 0.2 }}>
                                                        {dataProcess.shareType || '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Box>
                            ))}
                        </Paper>
                    </motion.div>
                ))}

                {/* Mensagem quando não há dados */}
                {(!filteredData || filteredData.length === 0) && (
                    <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            {t('ropa.process.noData')}
                        </Typography>
                    </Box>
                )}
            </Box>
        </Paper>
    )
}